import type { CSSProperties } from 'react';
import { Button } from '../../components/ui/Button';
import { EstadoError, EstadoVacio, ListaEsqueleto } from '../../components/ui/Estados';
import { Icon } from '../../components/ui/Icon';
import { Input, Select } from '../../components/ui/Input';
import { BarraFiltros, type ChipFiltro } from '../../components/layout/BarraFiltros';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import { EstadoComando } from '../../../domain/value-objects/EstadoComando';
import { formatearHora } from '../../utils/formato';
import { TarjetaActuador } from './TarjetaActuador';
import { useCentroControl } from './useCentroControl';
import estilos from './CentroControl.module.css';

export function CentroControl() {
  const c = useCentroControl();

  const chips: ChipFiltro[] = [];
  if (c.cascada.seleccion.clienteId) {
    const nombre = c.cascada.clientes.find((x) => x.id === c.cascada.seleccion.clienteId)?.nombre;
    chips.push({
      clave: 'cliente',
      etiqueta: `Cliente: ${nombre ?? c.cascada.seleccion.clienteId}`,
      quitar: () => c.cascada.elegirCliente(null),
    });
  }
  if (c.cascada.seleccion.areaId) {
    const nombre = c.cascada.areas.find((x) => x.id === c.cascada.seleccion.areaId)?.nombre;
    chips.push({
      clave: 'area',
      etiqueta: `Área: ${nombre ?? c.cascada.seleccion.areaId}`,
      quitar: () => c.cascada.elegirArea(null),
    });
  }
  if (c.cascada.seleccion.nodoId) {
    const nombre = c.cascada.nodos.find((x) => x.id === c.cascada.seleccion.nodoId)?.nombre;
    chips.push({
      clave: 'nodo',
      etiqueta: `Nodo: ${nombre ?? c.cascada.seleccion.nodoId}`,
      quitar: () => c.cascada.elegirNodo(null),
    });
  }
  if (c.busqueda.trim()) {
    chips.push({
      clave: 'busqueda',
      etiqueta: `Búsqueda: «${c.busqueda.trim()}»`,
      quitar: () => c.setBusqueda(''),
    });
  }

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina
        titulo="Control"
        descripcion="Acciona equipos a distancia y comprueba que la orden llegó."
        acciones={
          <Button
            variante="secundario"
            icono="refrescar"
            compactoEnMovil
            onClick={c.refrescar}
            cargando={c.recargando}
          >
            Actualizar
          </Button>
        }
      />

      {!c.puedeOperar && (
        <div className={estilos.aviso} role="status">
          <Icon nombre="candado" tamano={16} />
          {c.motivoSinPermiso ?? 'Tu usuario no puede accionar equipos.'} Puedes ver su estado, pero
          los controles están bloqueados.
        </div>
      )}

      <BarraFiltros
        cascada={c.cascada}
        chips={chips}
        acciones={
          chips.length > 0 && (
            <Button
              variante="fantasma"
              icono="cerrar"
              onClick={() => {
                c.cascada.limpiar();
                c.setBusqueda('');
              }}
            >
              Limpiar filtros
            </Button>
          )
        }
      >
        <Input
          etiqueta="Buscar"
          icono="buscar"
          placeholder="Actuador, nodo o área"
          value={c.busqueda}
          onChange={(e) => c.setBusqueda(e.target.value)}
        />

        <Select
          etiqueta="Tipo"
          placeholder="Todos los tipos"
          value={c.tipoFiltro}
          onChange={(e) => c.setTipoFiltro(e.target.value)}
          opciones={c.tiposDisponibles.map((tipo) => ({ valor: tipo, etiqueta: tipo }))}
        />

        <Select
          etiqueta="Estado"
          placeholder="Todos los estados"
          value={c.estadoFiltro}
          onChange={(e) => c.setEstadoFiltro(e.target.value as '' | 'on' | 'off')}
          opciones={[
            { valor: 'on', etiqueta: 'Encendidos' },
            { valor: 'off', etiqueta: 'Apagados' },
          ]}
        />
      </BarraFiltros>

      {c.cargando ? (
        <ListaEsqueleto filas={6} />
      ) : c.error ? (
        <EstadoError
          acciones={
            <Button variante="secundario" icono="refrescar" onClick={c.refrescar}>
              Reintentar
            </Button>
          }
        />
      ) : c.actuadores.length === 0 ? (
        <EstadoVacio
          icono="actuadores"
          titulo={c.total === 0 ? 'Sin actuadores' : 'Ningún actuador coincide'}
          descripcion={
            c.total === 0
              ? 'Cuando se registren equipos controlables aparecerán aquí.'
              : 'Prueba a quitar algún filtro para ver más resultados.'
          }
        />
      ) : (
        <div className={estilos.cuerpo}>
          <div className={estilos.rejilla}>
            {c.actuadores.map((a) => (
              <TarjetaActuador
                key={a.id}
                actuador={a}
                estado={c.estadoDe(a.id)}
                puedeOperar={c.puedeOperar}
                motivoSinPermiso={c.motivoSinPermiso}
                onEncender={() => void c.encender(a)}
                onApagar={() => void c.apagar(a)}
                onPulsar={(ms) => void c.pulsar(a, ms)}
              />
            ))}
          </div>

          <aside className={estilos.actividad}>
            <div className={estilos.actividadCabecera}>
              <span className={estilos.actividadTitulo}>Actividad</span>
              {c.actividad.length > 0 && (
                <Button variante="fantasma" tamano="sm" onClick={c.limpiarActividad}>
                  Limpiar
                </Button>
              )}
            </div>

            {c.actividad.length === 0 ? (
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-3)' }}>
                Aquí se irán registrando las órdenes que envíes y su resultado.
              </p>
            ) : (
              <div className={estilos.actividadLista}>
                {c.actividad.map((linea) => (
                  <div
                    key={linea.clave}
                    className={estilos.actividadItem}
                    style={
                      { '--color-orden': EstadoComando.from(linea.estado).color } as CSSProperties
                    }
                  >
                    <span className={estilos.actividadHora}>{formatearHora(linea.hora)}</span>
                    <span className={estilos.actividadTexto}>
                      {linea.texto}
                      <span className={estilos.actividadActuador}>{linea.actuador}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
